import { CurrencyMismatchError, InvalidDecimalError, InvalidPositionError } from '../errors';
import { HoldingValidationError, validateHoldingDraft } from '../holdingInput';
import type { ValidatedHolding } from '../holdingInput';
import { fromDecimal, isDecimalString, toDecimal } from '../money';
import { addToPosition, type Position } from '../position';
import { planSale, type ValidatedSale } from '../realized';
import type { Holding } from '../types';
import type { ParsedSlip, SlipCostBasis, SlipReview } from './types';

/**
 * สลิป → พอร์ต: ตัวเชื่อมระหว่าง "รายการซื้อขายหนึ่งครั้ง" กับระบบที่เก็บแค่ "สถานะปัจจุบัน"
 *
 * ## ทำไมต้องมีชั้นนี้
 *
 * PortTang เป็น **position-based ไม่ใช่ transaction ledger** (spec Section 1) — สลิปคือ
 * ธุรกรรม แต่ระบบไม่มีที่เก็บธุรกรรม สลิปจึงต้องถูกแปลงเป็น input ของ `addToPosition` /
 * `planSale` ที่มีอยู่แล้ว **ห้ามเปิด collection ธุรกรรมใหม่** ไม่งั้นจะกลายเป็น
 * ledger ครึ่งใบที่ไม่มีใครดูแล และ spec ตัดออกไปแล้วโดยตั้งใจ
 *
 * สลิปขายเขียนรายการขายลง `realized` ได้ เพราะเป็นทางเดียวกับปุ่ม "ขาย" — ไม่ใช่ที่เก็บ
 * ของสลิปโดยเฉพาะ (ดูเหตุผลที่ `realized.ts`)
 *
 * ## ทำไมสูตรอยู่ที่นี่ ไม่ใช่ในหน้าเว็บ
 *
 * ต้นทุนต่อหน่วยที่ได้จากสลิปคือ **เงิน** — ต้องผ่าน decimal.js เหมือนทุกอย่างในโปรเจกต์นี้
 * (spec Section 5.1) การหาร "ยอดสุทธิ ÷ จำนวน" ด้วย float ฝั่ง UI คือจุดที่ตัวเลขจะเพี้ยน
 * แบบที่ไม่มีใครจับได้จนกว่ามันจะไปโผล่เป็น PnL ผิด
 */

/** ผลของการเลือกว่าจะเอาตัวเลขไหนของสลิปเป็นต้นทุน */
export type UnitCostResult =
  | { state: 'ok'; unitCost: string }
  /** ข้อมูลที่ฐานนี้ต้องใช้อ่านไม่ออก — ผู้ใช้ต้องกรอกเอง ไม่ใช่ error */
  | { state: 'unavailable'; missing: 'quantity' | 'unitPrice' | 'netTotal' };

/**
 * คำนวณต้นทุนต่อหน่วยตามฐานที่เลือก
 *
 * `net-total` หารด้วยจำนวน จึงต้องกันตัวหารเป็นศูนย์ — สลิปที่อ่านจำนวนได้เป็น 0
 * แปลว่าอ่านผิด ไม่ใช่ซื้อ 0 หน่วย จึงถือว่าอ่านไม่ได้ ดีกว่าคืน Infinity
 */
export function resolveUnitCost(slip: ParsedSlip, basis: SlipCostBasis): UnitCostResult {
  if (basis === 'unit-price') {
    if (!isDecimalString(slip.unitPrice)) {
      return { state: 'unavailable', missing: 'unitPrice' };
    }
    return { state: 'ok', unitCost: fromDecimal(toDecimal(slip.unitPrice, 'slip.unitPrice')) };
  }

  if (!isDecimalString(slip.netTotal)) {
    return { state: 'unavailable', missing: 'netTotal' };
  }
  if (!isDecimalString(slip.quantity)) {
    return { state: 'unavailable', missing: 'quantity' };
  }

  const quantity = toDecimal(slip.quantity, 'slip.quantity');
  if (quantity.lessThanOrEqualTo(0)) {
    return { state: 'unavailable', missing: 'quantity' };
  }

  return {
    state: 'ok',
    unitCost: fromDecimal(toDecimal(slip.netTotal, 'slip.netTotal').div(quantity)),
  };
}

/** ผลการจับคู่สลิปกับของที่ถืออยู่แล้ว */
export type SlipMatch =
  /** แพลตฟอร์มและสัญลักษณ์ตรงกัน — ตัวเดียวกันแน่นอน */
  | { kind: 'exact'; holding: Holding }
  /**
   * สัญลักษณ์ตรงแต่คนละแพลตฟอร์ม
   *
   * ไม่ถือว่าตรง เพราะ VOO ที่ Dime กับ VOO ที่ Jitta Wealth เป็นคนละ holding
   * ในระบบนี้โดยตั้งใจ (แยกตามแพลตฟอร์มตาม spec Section 4.3) — แต่ต้องบอก UI
   * ให้เตือน ไม่งั้นผู้ใช้จะสร้างรายการซ้ำโดยไม่รู้ว่ามีของเดิมอยู่
   */
  | { kind: 'other-platform'; holdings: Holding[] }
  | { kind: 'none' };

/** เทียบแบบไม่สนตัวพิมพ์และช่องว่างหัวท้าย — `dime` กับ `Dime` คือที่เดียวกัน */
function sameText(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

export function matchSlipHolding(
  input: { symbol: string; platform: string },
  holdings: readonly Holding[],
): SlipMatch {
  const bySymbol = holdings.filter((holding) => sameText(holding.symbol, input.symbol));
  if (bySymbol.length === 0) {
    return { kind: 'none' };
  }

  const exact = bySymbol.find((holding) => sameText(holding.platform, input.platform));
  if (exact !== undefined) {
    return { kind: 'exact', holding: exact };
  }

  return { kind: 'other-platform', holdings: bySymbol };
}

/**
 * สิ่งที่จะเกิดขึ้นกับพอร์ตถ้ากดยืนยัน — ผู้เรียกเอาไปเขียนลง Firestore ตรง ๆ
 *
 * `next: null` ใน `reduce` แปลว่าขายหมดพอดี ต้องลบ holding ทิ้ง (spec Section 5.4)
 * `sale` คือรายการขายที่ต้องเขียนคู่กันใน batch เดียว (spec Section 5.5)
 */
export type SlipPlan =
  | { kind: 'add'; holding: Holding; next: Position }
  | { kind: 'reduce'; holding: Holding; next: Position | null; sale: ValidatedSale }
  | { kind: 'create'; holding: ValidatedHolding }
  /** ทำต่อไม่ได้ พร้อมเหตุผลที่เอาไปแสดงบนจอได้เลย */
  | { kind: 'blocked'; message: string };

/**
 * แปลงค่าที่ผู้ใช้ยืนยันแล้วเป็นแผนการเขียนลงพอร์ต
 *
 * **ไม่เขียนอะไรเอง** — คืนแผนออกไปให้ UI แสดง before/after แล้วรอการกดยืนยัน
 * เหตุผลอยู่ใน PLAN.md B1: เจ้าของกรอกต้นทุนผิดหน่วยไป 6 ครั้งจาก 16 การให้ OCR
 * เขียนลงพอร์ตเองจะเปลี่ยนความผิดพลาดนั้นจากที่เกิดเป็นครั้ง ๆ ให้เกิดอัตโนมัติ
 */
export function planSlipApply(review: SlipReview, holdings: readonly Holding[]): SlipPlan {
  // เงินสดไม่มีการซื้อขายให้ถัวเฉลี่ย — ต้นทุนต่อหน่วยเป็น 1 เสมอโดยนิยาม
  if (review.assetType === 'cash') {
    return {
      kind: 'blocked',
      message: 'สลิปซื้อขายใช้กับรายการเงินสดไม่ได้ ให้แก้จำนวนเงินโดยตรง',
    };
  }

  const match = matchSlipHolding(review, holdings);

  if (match.kind === 'exact') {
    return planAgainstHolding(review, match.holding);
  }

  if (review.side === 'sell') {
    // ขายของที่ระบบไม่รู้ว่าถืออยู่ = ลดจำนวนจากศูนย์ ซึ่งไม่มีความหมาย
    // และไม่ใช่เรื่องที่ควรเดาแทน — ของอาจอยู่คนละแพลตฟอร์มหรือยังไม่เคยกรอกเข้ามา
    return {
      kind: 'blocked',
      message: `ไม่พบ ${review.symbol} ที่ ${review.platform} ในพอร์ต จึงบันทึกการขายให้ไม่ได้`,
    };
  }

  try {
    return {
      kind: 'create',
      holding: validateHoldingDraft({
        platform: review.platform,
        assetType: review.assetType,
        symbol: review.symbol,
        displayName: review.displayName ?? null,
        quantity: review.quantity,
        avgCost: review.unitCost,
        currency: review.currency,
        note: review.note ?? null,
        coingeckoId: review.coingeckoId ?? null,
      }),
    };
  } catch (error) {
    if (error instanceof HoldingValidationError) {
      return { kind: 'blocked', message: error.message };
    }
    throw error;
  }
}

/**
 * สลิปตรงกับของที่ถืออยู่ → ถัวเฉลี่ยหรือขาย
 *
 * สกุลเงินต้องตรงกับ holding เดิม ถ้าไม่ตรงให้หยุด ไม่แปลงให้อัตโนมัติ (spec Section 5.3)
 * — ซื้อ VOO เพิ่มด้วยเงินบาทบนสลิป ขณะที่ต้นทุนเดิมเก็บเป็น USD แล้วปล่อยให้ถัวรวมกัน
 * จะได้ต้นทุนเฉลี่ยที่ไม่มีความหมายในสกุลไหนเลย ฝั่งขายก็เหมือนกัน: ราคาขายต่างสกุล
 * ลบกับต้นทุนไม่ได้
 */
function planAgainstHolding(review: SlipReview, holding: Holding): SlipPlan {
  const current: Position = {
    quantity: holding.quantity,
    avgCost: holding.avgCost,
    currency: holding.currency,
  };

  try {
    if (review.side === 'sell') {
      const { next, sale } = planSale(holding, {
        quantity: review.quantity,
        price: review.unitCost,
        currency: review.currency,
      });
      return { kind: 'reduce', holding, next, sale };
    }

    return {
      kind: 'add',
      holding,
      next: addToPosition(current, {
        quantity: review.quantity,
        price: review.unitCost,
        currency: review.currency,
      }),
    };
  } catch (error) {
    // จำกัดเฉพาะความผิดพลาดของ "ข้อมูลที่กรอกมา" เท่านั้น อย่างอื่นปล่อยให้ throw ต่อ
    // ไม่งั้นบั๊กในสูตรจะถูกกลืนกลายเป็นข้อความเตือนที่ผู้ใช้แก้ตามไม่ได้
    if (
      error instanceof InvalidPositionError ||
      error instanceof CurrencyMismatchError ||
      error instanceof InvalidDecimalError
    ) {
      return { kind: 'blocked', message: error.message };
    }
    throw error;
  }
}
